import { Component, ViewChild, Input, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions, SnackType, imagePath, imgTypes } from '@global/GlobalFunctions';
import { LevelAsset, AssetsDataService, ILevelAsset } from '@services/assets-data.service';
import { IOrganisation, IOrganisationsHierarchy, OrganisationDataService, OrganisationsHierarchy } from '@services/organisation-data.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Subject, combineLatest, distinctUntilChanged, filter, finalize, firstValueFrom, take, takeUntil, tap } from 'rxjs';
import { ListViewComponent } from '@syncfusion/ej2-angular-lists';
import { EditHierarchyLocationComponent } from '../../../dialogs/edit-hierarchy-location/edit-hierarchy-location.component';
import { EditHierarchyAssetComponent } from '../../../dialogs/edit-level-asset/edit-level-asset.component';
import { AuthenticationService, WebAppUser } from '@services/authentication.service';
import { EditAssetDatapointsComponent, EditAssetSettingsDialogData } from 'src/app/dialogs/edit-asset-datapoints/edit-asset-datapoints.component';
import { CommentDialogComponent } from 'src/app/dialogs/comment-dialog/comment-dialog.component';
import { AssetSearchDialogComponent } from 'src/app/dialogs/asset-search-dialog/asset-search-dialog.component';
import { BreadcrumbItemModel } from '@syncfusion/ej2-navigations';
import { BreadcrumbComponent, TreeViewModule } from '@syncfusion/ej2-angular-navigations';
import { AccountFeatures, AccountsService, IAccount, IFeature } from '@services/accounts.service';
import { Title } from '@angular/platform-browser';
import { query } from 'express';
import { MatCheckbox } from '@angular/material/checkbox';

/** 
 * MyBreadcrumbItem
 * extends BreadcrumbItemModel
 */
interface MyBreadcrumbItem extends BreadcrumbItemModel {
	/** Optional data property to hold additional information */
	data?: { id: number; uid: string; type: string;[key: string]: any };
}

/** HierarchyDashboardComponent */
@Component({
	selector: 'app-hierarchy-dashboard',
	templateUrl: './hierarchy-dashboard.component.html',
	styleUrls: ['./hierarchy-dashboard.component.scss']
})
export class HierarchyDashboardComponent implements OnDestroy {

	/** Path to Assets Images */
	public imagePathAssets(): string {
		return imagePath(this.accountGUID, imgTypes.organisationassets).toLowerCase()
	}

	/** Path to Organisation Images */
	public imagePathOrganisations(): string {
		return imagePath(this.accountGUID, imgTypes.organisations).toLowerCase()
	}

	/** Path to Sub Locations Images */
	public imagePathSubLocations(): string {
		return imagePath(this.accountGUID, imgTypes.sublocations).toLowerCase()
	}

	/** child */
	@Input() child: OrganisationsHierarchy;

	/** listviewInstance */
	@ViewChild('listviewInstance') listviewInstance: ListViewComponent;
	/** pageBreadcrumbs */
	@ViewChild('pageBreadcrumbs') pageBreadcrumbs: BreadcrumbComponent;

	public cssClass: string = 'e-list-template';
	/** Observable Subject Finalise */
	private finalise = new Subject<void>();
	/** Route Asset Parameter */
	public hierarchicalLocation?: OrganisationsHierarchy
	/** Asset Children */
	public locationChildren: OrganisationsHierarchy[]
	/** Assets List */
	public locationAssets: LevelAsset[] = [];
	/** set the Selected nodes to the TreeView */
	public locationParents: OrganisationsHierarchy[];
	/** breadcrumbItems */
	public breadcrumbItems: MyBreadcrumbItem[] = []

	/** Selected Text */
	public selectedLocation: string = ""
	/** selectedAsset */
	public selectedAsset?: ILevelAsset;
	/** Data to Tree View */
	public hierarchicalData: OrganisationsHierarchy[]
	/** Fields */
	public fields: any
	/** Child Path */
	public childPath: string = ""
	/** Navigation area Is Colapsed */
	public isCollapsed = false;
	/** accountID */
	public accountGUID: string
	/** OrganisationID */
	public OrganisationUID: string

	/** thisUser */
	public thisUser: WebAppUser;

	/** flaggedAssets */
	public flaggedAssets: any[] = [];
	/** getFlaggedAssets */
	public favouriteAssets: LevelAsset[] = [];
	/** getFlaggedAssets */
	public selectedAccount: IAccount;
	/** selectedOrganisation */
	public selectedOrganisation: IOrganisation;
	/** Account Features */
	public AccountFeatures = AccountFeatures;

	/** hideButtons */
	public hideButtons: boolean = false;

	/** TitlePrefix */
	public TitlePrefix: string = "";

	/** selectingCompare - Git#158 */
	public selectingCompare: boolean = false;

	/** selectedCompareAssets - Git#158 */
	public selectedCompareAssets: LevelAsset[] = [];

	/** Math */
	public Math = Math;

	/**
	 *  Default Constructor
	 * @param organisationDataService Organisation Data
	 * @param assetsDataService AssetsDataService
	 * @param router router
	 * @param route route
	 * @param siteSubscriptions site Subscriptions
	 * @param globalFunctions global Functions
	 * @param dialog dialog
	 * @param authenticationService authenticationService
	 */
	constructor(
		public organisationDataService: OrganisationDataService,
		public assetsDataService: AssetsDataService,
		private router: Router,
		private route: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private globalFunctions: GlobalFunctions,
		public dialog: MatDialog,
		public authenticationService: AuthenticationService,
		public title: Title
	) {

		// this.TitlePrefix = this.title.getTitle();

		combineLatest([
			this.authenticationService.currentUser$.pipe(distinctUntilChanged()),
			this.route.paramMap.pipe(distinctUntilChanged())
		]).pipe(
			takeUntil(this.finalise),
			// Ensure that subscriptions are not null or undefined
			filter(([currentUser, paramMap]) =>
				!!currentUser && paramMap.has('sublocation'))
		).subscribe({
			next: async ([currentUser, paramMap]) => {

				let [selectedAccount, selectedAccountGUID, selectedOrganisation, selectedOrganisationUID] = await firstValueFrom(
					combineLatest([
						this.siteSubscriptions.selectedAccount$.pipe(take(1)),
						this.siteSubscriptions.selectedAccountGUID$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisation$.pipe(take(1)),
						this.siteSubscriptions.selectedOrganisationUID$.pipe(take(1))
					]
					)
				);

				this.thisUser = currentUser
				this.accountGUID = selectedAccountGUID;
				this.selectedAccount = selectedAccount;
				this.OrganisationUID = selectedOrganisationUID;
				this.selectedOrganisation = selectedOrganisation;

				let urlAccountID = this.router.url.split('/')[1];

				if (this.accountGUID.toLowerCase() != urlAccountID.toLowerCase() && this.router.url.includes('location') && !this.router.url.includes('routing#')) {
					this.router.navigateByUrl(`routing#${this.router.url}`);
				}

				var paramSublocation = paramMap.get('sublocation') ?? "";
				this.subscribeHierarchicalData(paramSublocation)
				// this.getFavouriteAssets();
				this.getFlaggedAssets();
				// this.subscribeSelectedAsset()

				/* reset comparing selection variables */
				this.selectingCompare = false;
				this.selectedCompareAssets = [];
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: Error in one of the combined subscriptions", false);
			}
		});
	}

	/**
	 * Page On Destroy Event Handler
	 */
	ngOnDestroy(): void {
		try {
			// Unsubscribe from all subscriptions
			this.finalise.next();
			this.finalise.complete();
		}
		catch { }
	}

	/**
	 * getHierarchicalData
	 * @param paramAsset
	 */
	public async subscribeHierarchicalData(subLocation: string) {

		this.locationParents = [];
		this.locationChildren = [];
		this.breadcrumbItems = [];

		// const hierarchy = await firstValueFrom(
		// 	this.siteSubscriptions.hierarchicalData$.pipe(
		// 		filter(result => !!result)
		// 	)
		// );

		// if (hierarchy) {
		// 	this.locationAssets = [];
		// 	this.hierarchicalData = <IOrganisationsHierarchy[]>JSON.parse(hierarchy);
		// 	if (subLocation && (this.hierarchicalData.length > 0)) {
		// 		this.fields = { dataSource: this.hierarchicalData, value: 'Uid', text: 'Name', child: 'SubChild' };
		// 		this.hierarchicalLocation = this.organisationDataService.findObjectByUID(subLocation, this.hierarchicalData)

		// 		/*** Route Found */
		// 		if (this.hierarchicalLocation) {
		// 			this.locationAssets = [];
		// 			this.hierarchicalData = <IOrganisationsHierarchy[]>JSON.parse(hierarchy);
		// 			if (subLocation && (this.hierarchicalData.length > 0)) {
		// 				this.fields = { dataSource: this.hierarchicalData, value: 'Uid', text: 'Name', child: 'SubChild' };
		// 				this.hierarchicalLocation = this.organisationDataService.findObjectByUID(subLocation, this.hierarchicalData)

		// 				/*** Route Found */
		// 				if (this.hierarchicalLocation) {
		// 					try {
		// 						this.siteSubscriptions.SetSelectedhierarchicalLocation(this.hierarchicalLocation);
		// 						this.locationParents = this.organisationDataService.getParents(this.hierarchicalData[0], this.hierarchicalLocation.Uid);
		// 						try {
		// 							const newItems = this.buildBreadcrumbItems(this.hierarchicalLocation, this.locationParents)
		// 							this.breadcrumbItems = [...newItems];  // spreads into a new array reference
		// 							this.pageBreadcrumbs.activeItem = '';
		// 						} catch { }
		// 						// this.cd.detectChanges();

		// 						if (this.hierarchicalLocation.SubChild) {
		// 							this.locationChildren = this.organisationDataService.getChildren(this.hierarchicalLocation);
		// 							if (this.locationChildren) {
		// 								for (const child of this.locationChildren) {
		// 									let subAssets = <LevelAsset[]>this.assetsDataService.findAssets(child);
		// 									let subwatches = <LevelAsset[]>this.assetsDataService.findAssets(child, true);
		// 									let subActions = <LevelAsset[]>this.assetsDataService.findAssets(child, false, true);
		// 									child.TotalAssetCount = subAssets.length;
		// 									child.TotalWatchCount = subwatches.length;
		// 									child.TotalActionCount = subActions.length;
		// 								}
		// 							}
		// 						}
		// 						if (this.hierarchicalLocation.Assets) {
		// 							this.locationAssets = this.hierarchicalLocation.Assets;
		// 							this.subscribeSelectedAsset();
		// 						}
		// 					}
		// 					catch (error) { console.error(error); }
		// 					this.selectedLocation = this.hierarchicalLocation.Name
		// 				}
		// 				else {
		// 					// console.log("Route Not Found");
		// 					/** Need to Reload the current URL */
		// 				}
		// 			}
		// 			else { this.locationParents = [] }
		// 		}
		// 	}
		// }

		this.siteSubscriptions.hierarchicalData$.pipe(
			// tap(() => {
			// 	this.locationParents = [];
			// 	this.locationChildren = [];
			// 	this.breadcrumbItems = [];
			// }),
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((data) => !!data)
		).subscribe({
			next: (data: string) => {
				this.locationAssets = [];
				this.hierarchicalData = <IOrganisationsHierarchy[]>JSON.parse(data);
				if (subLocation && (this.hierarchicalData.length > 0)) {
					this.fields = { dataSource: this.hierarchicalData, value: 'Uid', text: 'Name', child: 'SubChild' };
					this.hierarchicalLocation = this.organisationDataService.findObjectByUID(subLocation, this.hierarchicalData)

					/*** Route Found */
					if (this.hierarchicalLocation) {
						try {
							this.siteSubscriptions.SetSelectedhierarchicalLocation(this.hierarchicalLocation);
							this.locationParents = this.organisationDataService.getParents(this.hierarchicalData[0], this.hierarchicalLocation.Uid);
							try {
								const newItems = this.buildBreadcrumbItems(this.hierarchicalLocation, this.locationParents)
								this.breadcrumbItems = [...newItems];  // spreads into a new array reference
								this.pageBreadcrumbs.activeItem = '';
							} catch { }
							// this.cd.detectChanges();

							if (this.hierarchicalLocation.SubChild) {
								this.locationChildren = this.organisationDataService.getChildren(this.hierarchicalLocation);
								if (this.locationChildren) {
									for (const child of this.locationChildren) {
										let subAssets = <LevelAsset[]>this.assetsDataService.findAssets(child);
										let subwatches = <LevelAsset[]>this.assetsDataService.findAssets(child, true);
										let subActions = <LevelAsset[]>this.assetsDataService.findAssets(child, false, true);
										child.TotalAssetCount = subAssets.length;
										child.TotalWatchCount = subwatches.length;
										child.TotalActionCount = subActions.length;
									}
								}
							}
							if (this.hierarchicalLocation.Assets) {
								this.locationAssets = this.hierarchicalLocation.Assets;
								this.subscribeSelectedAsset();
							}

							// this.title.setTitle(`${this.TitlePrefix} - ${this.hierarchicalLocation.Name}`);

						}
						catch (error) { console.error(error); }
						this.selectedLocation = this.hierarchicalLocation.Name
					}
					else {
						// console.log("Route Not Found");
						/** Need to Reload the current URL */
					}
				}
				else { this.locationParents = [] }
			},
			error: (error) => {
				this.globalFunctions.HandleError(error, "Error: this.siteSubscriptions.hierarchicalData$", false)
			},
			//complete: () => {console.log("this.siteSubscriptions.hierarchicalData$.subscribe: Complete");}
		});

	}


	/**
	 * Build the Breadcrumb Items
	 * @param hierarchicalLocation 
	 * @param locationParents 
	 * @returns 
	 */
	public buildBreadcrumbItems(hierarchicalLocation: OrganisationsHierarchy, locationParents: OrganisationsHierarchy[]) {

		let breadcrumbItems = [];

		for (const breadCrum of this.locationParents) {
			if (breadcrumbItems.length == 0) {
				breadcrumbItems.push(
					{ text: breadCrum.Name, iconCss: 'e-icons e-home', data: { id: breadCrum.Id, uid: breadCrum.Uid, type: 'root' } }
				)
			} else {
				//  iconCss: 'e-icons e-folder',
				breadcrumbItems.push(
					{ text: breadCrum.Name, data: { id: breadCrum.Id, uid: breadCrum.Uid, type: 'location' } }
				)
			}
		}

		if ((locationParents && locationParents.length > 0) && locationParents[0].Uid != this.hierarchicalLocation?.Uid) {
			breadcrumbItems.push(
				{
					text: hierarchicalLocation.Name,
					data: { id: hierarchicalLocation.Id, uid: hierarchicalLocation.Uid, type: 'location' }
				}
			)
		}

		return breadcrumbItems

	}

	/**
	 * subscribeSelectedAsset
	 */
	subscribeSelectedAsset() {
		// Track the selected Asset
		this.siteSubscriptions.selectedAsset$.pipe(
			tap(() => { this.selectedAsset = undefined }),
			takeUntil(this.finalise), distinctUntilChanged(),
			filter((asset) => !!asset)
		).subscribe({
			next: (assetJSON: any) => {
				// let asset2: LevelAsset = JSON.parse(asset)
				// let result = this.locationAssets.find((x: ILevelAsset) => x.AssetID == asset2.AssetID)
				// if (result) {
				// 	this.selectedAsset = asset2;
				// 	this.showAsset(asset2);
				// }
				let asset: LevelAsset;
				if (typeof (assetJSON) == 'string') {
					asset = JSON.parse(assetJSON)
					this.selectedAsset = asset
				} else {
					this.selectedAsset = assetJSON
				}
				if (this.selectedAsset) {
					this.showAsset(this.selectedAsset)
				}
			},
			error: (error) => { }
		});
	}

	/**
	 * Bind the Select event
	 * @param asset
	 */
	public assetSelect(asset: ILevelAsset) {
		try {
			let selAsset: any = asset.AssetID;
			this.siteSubscriptions.SetSelectedAsset(asset);
		} catch (error) { console.error(error); }
	}

	/**
	 * Navigate to Asset
	 * @param assetID
	 */
	private showAsset(asset: ILevelAsset) {
		try {
			// Create the new child route segment
			let currentUrl = this.router.url.split("/");
			let newChildSegment = currentUrl.slice(0, 6).join("/");
			// Navigate to the new URL with the changed child route
			this.router.navigate([`${newChildSegment}/asset/${asset.AssetID}`], { relativeTo: this.route.parent });
		} catch (error) { console.error(error); }
	}

	/**
	 * Bind the Select event
	 * @param locationUID
	 */
	public locationSelect(locationUID: any) {
		try {
			let sellocation: any = locationUID;
			this.navigate(sellocation);
			// this.numberRND = Math.random()
		} catch (error) { console.error(error); }
	}

	/**
	 * Handle item click event
	 * @param event
	 */
	onItemClick(event: any) {
		const locationUID = event.item.data['uid'];
		// console.log('Clicked breadcrumb item:', event.item);
		// console.log('Text:', event.item.text);
		// console.log('URL:', event.item.url);

		try {
			let sellocation: any = locationUID;
			this.navigate(sellocation);
			// this.numberRND = Math.random()
		} catch (error) { console.error(error); }

	}

	/**
	 * Navigate to Sub Location
	 * @param locationUID
	 */
	private navigate(locationUID: string) {
		try {
			// Create the new child route segment
			let currentUrl = this.router.url.split("/");
			currentUrl[5] = locationUID;
			let newChildSegment = currentUrl.slice(0, 6).join("/");
			// Navigate to the new URL with the changed child route
			this.router.navigate([newChildSegment], { relativeTo: this.route.parent });
			this.siteSubscriptions.SetSelectedSubLocation(locationUID)
		} catch (error) { console.error(error); }
	}

	/**
	 * Add new Asset
	 * @param subLocation
	 */
	addAsset(subLocation: OrganisationsHierarchy) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = <ILevelAsset>{};

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyAssetComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			Object.assign(_edit, result);
			this.assetsDataService.postOrganisationAssets$(this.accountGUID, this.OrganisationUID, subLocation.Uid, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.subscribeOrganisationStructure(this.accountGUID, this.OrganisationUID)
				}
			});
		});

	}

	/**
	 * Edit the Company details
	 * @param editAsset
	 */
	editAsset(editAsset: ILevelAsset): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...editAsset };

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyAssetComponent, {
			data: _edit,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			Object.assign(_edit, result);
			this.assetsDataService.patchOrganisationAssets$(this.accountGUID, this.OrganisationUID, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					Object.assign(editAsset, result);
					this.subscribeOrganisationStructure(this.accountGUID, this.OrganisationUID)
					this.globalFunctions.DisplaySnackBar({
						message: "Asset Details Updated",
						type: SnackType.Info,
						duration: 1000
					});
				}
			});
		});
	}

	/**
	 * Edit the Company details
	 * @param editAsset
	 */
	showAssetDatapoints(editAsset: ILevelAsset): void {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the Asset Information
		var _edit = { ...editAsset };

		let dialogData = <EditAssetSettingsDialogData>{
			assetItem: _edit,
			OrganisationUID: this.OrganisationUID,
			selectedAccountUID: this.accountGUID
		}

		// Display the dialog box
		const dialogRef = this.dialog.open(EditAssetDatapointsComponent, {
			data: dialogData,
		});

		// Update the original Asset with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			this.globalFunctions.DisplaySnackBar({
				message: "Asset Settings Updated",
				type: SnackType.Info,
				duration: 1000
			});
		});
	}

	/**
	 * Add new location
	 * @param subLocation
	 */
	addLocation(subLocation: OrganisationsHierarchy) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		// Create a 'Local'/shallow copy of the location Information
		var _edit = <IOrganisationsHierarchy>{};

		// Display the dialog box
		const dialogRef = this.dialog.open(EditHierarchyLocationComponent, {
			data: _edit,
		});

		// Update the original Location with the result
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// console.log('The dialog was closed');
			// Check we have a result to work with
			Object.assign(_edit, result);
			this.organisationDataService.postOrganisationLocation$(this.accountGUID, this.OrganisationUID, subLocation, _edit).pipe(
				takeUntil(fetchDataSubject), distinctUntilChanged(),
				filter((response) => !!response),
				finalize(() => {
					// Ensure the subscription is terminated and the subject is completed
					fetchDataSubject.next();
					fetchDataSubject.complete();
				})
			).subscribe({
				next: (response) => {
					this.subscribeOrganisationStructure(this.accountGUID, this.OrganisationUID)
				},
				error: (error) => {
					if (error["status"] != 404) {
						this.globalFunctions.HandleError(error, "Error: this.organisationDataService.postOrganisationLocation.subscribe$", false)
					}
				},
			});
		});
	}

	/**
	 * Get the Organisation Structure
	 * @param AccountID AccountID
	 * @param OrganisationID OrganisationID
	 */
	async subscribeOrganisationStructure(AccountID: string, OrganisationUID: string) {

		try {
			let structure = await firstValueFrom(
				this.organisationDataService.getOrganisationStructure$(AccountID, OrganisationUID)
					.pipe(filter((response) => !!response)));
			if (structure) {
				var OrganisationStructure = <IOrganisationsHierarchy[]>JSON.parse(structure);
				this.siteSubscriptions.SethierarchicalData(OrganisationStructure);
			}
		}
		catch (error: any) {
			if (error["status"] != 404) {
				this.globalFunctions.HandleError(error, "Error: OrganisationData.getOrganisationStructure$(OrganisationUID).subscribe$", false)
			}
		}

	}

	/**
	 * Edit the Location details
	 * @param imageURL
	 */
	viewImage(imageURL: string): void {
		this.globalFunctions.viewImageFullSize(imageURL);
	}

	/**
	 * getFavouriteAssets
	 */
	async getFavouriteAssets() {
		try {
			this.favouriteAssets = [];
			let result = await firstValueFrom(
				this.assetsDataService.getUserFavouriteAssets$(this.accountGUID, this.OrganisationUID)
					.pipe(filter((response) => !!response)));
			if (result[0].Data) {
				this.favouriteAssets = JSON.parse(result[0].Data);
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getUserFavouriteAssets$", false);
		}
	}

	/**
	 * checkIfAssetFavourite
	 * @param asset 
	 */
	checkIfAssetFavourite(asset: LevelAsset): boolean {
		if (this.favouriteAssets.length > 0) {
			return this.favouriteAssets.some((x: LevelAsset) => x.AssetID == asset.AssetID);
		}
		return false;
	}

	/**
	 * addFavourite
	 * @param asset asset
	 */
	addFavourite(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.postUserFavouriteAssets$(this.accountGUID, this.OrganisationUID, asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.favouriteAssets.push(asset);
				this.globalFunctions.DisplaySnackBar(
					{
						message: "Favourite Added",
						duration: 1000
					}
				);
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postUserFavouriteAssets$", false); },
		});
	}

	/**
	 * removeFavourite
	 * @param asset asset
	 */
	removeFavourite(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.deleteUserFavouriteAssets$(this.accountGUID, this.OrganisationUID, asset.AssetID, this.thisUser.Sub).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.favouriteAssets = this.favouriteAssets.filter((x: LevelAsset) => x.AssetID != asset.AssetID);
				this.globalFunctions.DisplaySnackBar(
					{
						message: "Favourite Removed",
						duration: 1000
					}
				);
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.deleteUserFavouriteAssets$", false); },
		});
	}

	/**
	 * getFlaggedAssets
	 */
	async getFlaggedAssets() {
		try {
			this.flaggedAssets = [];
			let result = await firstValueFrom(
				this.assetsDataService.getFlaggedAssets$(this.accountGUID, this.OrganisationUID)
					.pipe(filter((response) => !!response)));
			if (result[0].Data) {
				this.flaggedAssets = JSON.parse(result[0].Data);
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, "Error: this.assetsDataService.getFlaggedAssets$", false);
		}
	}

	/**
	 * checkIfAssetFlagged
	 */
	checkIfAssetFlagged(asset: LevelAsset): boolean {
		if (this.flaggedAssets.length > 0) {
			let temp = this.flaggedAssets.find((x: any) => x.AssetID == asset.AssetID);
			if (temp) {
				return true;
			}
		}
		return false;
	}

	/**
	 * addFlagged
	 * @param asset asset
	 */
	addFlagged(asset: LevelAsset) {
		const dialogRef = this.dialog.open(CommentDialogComponent, {
			width: '500px',
			data: {
				"HeaderText": "Add Flagged Asset",
				"DialogText": "Explain why you want to flag " + asset.Name + " - " + asset.Description
			}
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			// Perform the delete action with the comment
			// console.log('The dialog was closed with comment:', result.comment);
			// Create a local Subject to control the subscription lifecycle
			if (result.comment) {
				const fetchDataSubject = new Subject<void>();

				this.assetsDataService.postFlaggedAsset$(this.accountGUID, this.OrganisationUID, asset.AssetID, result.comment).pipe(
					takeUntil(fetchDataSubject), distinctUntilChanged(),
					filter((response) => !!response),
					finalize(() => {
						// Ensure the subscription is terminated and the subject is completed
						fetchDataSubject.next();
						fetchDataSubject.complete();
					})
				).subscribe({
					next: (result) => {
						this.flaggedAssets.push(asset);
						this.globalFunctions.DisplaySnackBar(
							{
								message: "Flag Added",
								duration: 1000
							}
						);
					},
					error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.postFlaggedAsset$", false); },
				});
			}
		});
	}

	/**
	 * removeFlaggedAsset
	 * @param asset asset
	 */
	removeFlaggedAsset(asset: LevelAsset) {
		// Create a local Subject to control the subscription lifecycle
		const fetchDataSubject = new Subject<void>();

		this.assetsDataService.deleteFlaggedAsset$(this.accountGUID, this.OrganisationUID, asset.AssetID).pipe(
			takeUntil(fetchDataSubject), distinctUntilChanged(),
			filter((response) => !!response),
			finalize(() => {
				// Ensure the subscription is terminated and the subject is completed
				fetchDataSubject.next();
				fetchDataSubject.complete();
			})
		).subscribe({
			next: (result) => {
				this.flaggedAssets = this.flaggedAssets.filter((x: any) => x.AssetID != asset.AssetID);
				this.globalFunctions.DisplaySnackBar(
					{
						message: "Flag Removed",
						duration: 1000
					}
				);
			},
			error: (error) => { this.globalFunctions.HandleError(error, "Error: this.assetsDataService.deleteFlaggedAsset$", false); },
		});
	}

	/**
	 * searchForAsset
	 */
	searchForAsset() {
		let assetList: LevelAsset[] = this.flattenAssets(this.hierarchicalData);
		const dialogRef = this.dialog.open(AssetSearchDialogComponent, {
			data: {
				assetList: assetList,
				hierarchy: this.hierarchicalData,
				currentLocation: { ...this.hierarchicalLocation }
			}
		});
		dialogRef.afterClosed().pipe(
			filter(result => !!result)
		).subscribe(result => {
			let asset = assetList.find((x: LevelAsset) => x.AssetID == result.asset);
			if (asset) {
				this.goToFunctionalLocation(asset);
			}
		});
	}

	/** Getting list of assets from an input hierarchy */
	flattenAssets(hierarchy: OrganisationsHierarchy[]): any[] {
		let result: any[] = [];

		// Check if hierarchy is a valid array before proceeding
		if (!Array.isArray(hierarchy)) {
			return result;
		}

		// Iterate over each item in the input hierarchy
		for (const item of hierarchy) {
			// Check if the item has a non-empty 'Asset' property
			if (item.Assets && item.Assets.length > 0) {
				result = result.concat(item.Assets);
			}

			// Recursively add all 'Asset' properties of the item's subchildren
			result = result.concat(this.flattenAssets(item.SubChild));
		}

		return result;
	}

	/**
	 * goToFunctionalLocation
	 * @param asset asset
	 */
	goToFunctionalLocation(asset: LevelAsset) {
		this.siteSubscriptions.SetSelectedAsset(asset);
		this.router.navigate([`/${this.accountGUID}/organisation/${this.OrganisationUID}/location/${asset.ParentUID}/asset/${asset.AssetID}`]);
	}

	/**
	 * toggleHideButtons
	 * @param hide hide 
	 */
	toggleHideButtons(hide: boolean) {
		this.hideButtons = hide;
	}

	/**
	 * checkFeature
	 * @param feature feature   
	 * @returns 
	 */
	checkFeature(feature: AccountFeatures): boolean {
		if (this.selectedAccount && this.selectedAccount.Features) {
			return AccountsService.checkFeature(feature, this.selectedAccount)
			// return this.selectedAccount.Features.some((x: any) => x.FeatureID == feature && x.Enabled);
		}
		return false;
	}

	/**
	 * toggleAssetSelectedForCompare
	 * @param asset asset 
	 */
	toggleAssetSelectedForCompare(event: any, asset: LevelAsset, checkBox: MatCheckbox) {
		if (event.checked) {
			if (this.selectedCompareAssets.length < 5) {
				this.selectedCompareAssets.push(asset);
			} else {
				event.cancel = true;
				checkBox.checked = false;
				this.globalFunctions.DisplaySnackBar({
					message: "Only 5 assets can be compared",
					duration: 2000,
					action: ""
				});
			}
		} else {
			this.selectedCompareAssets = this.selectedCompareAssets.filter((x: LevelAsset) => x.AssetID != asset.AssetID);
		}
	}

	/**
	 * compareAssets
	 */
	compareAssets() {
		let queryParams: any = {};
		this.selectedCompareAssets.forEach((x: LevelAsset, i: number) => queryParams[`asset${i + 1}`] = `${x.AssetID}`);
		this.router.navigate([
			this.selectedAccount.GUID,
			'organisation',
			this.selectedOrganisation.Uid,
			'compare'
		], { queryParams: queryParams });
	}

	/**
	 * checkIfAssetIncludedCompare
	 * @param asset asset 
	 */
	checkIfAssetIncludedCompare(asset: LevelAsset): boolean {
		return this.selectedCompareAssets.some((x: LevelAsset) => x.AssetID == asset.AssetID);
	}

	/**
	 * toggleSelectAllForCompare
	 * @param event event 
	 */
	toggleSelectAllForCompare(event: any) {
		if (event.checked) {
			this.selectedCompareAssets = this.locationAssets.slice(0, 5);
		} else {
			this.selectedCompareAssets = [];
		}
	}
}
